import { CALL_API } from '../middleware/api';

export const FETCHING_USERS = Symbol('FETCHING_USERS');
export const FETCHED_USERS_FAILURE = Symbol('FETCHED_USERS_FAILURE');
export const FETCHED_USERS_SUCCESS = Symbol('FETCHED_USERS_SUCCESS');
export const SET_USER_LIST_FILTERS = Symbol('SET_USER_LIST_FILTERS');
export const FETCHED_SUGGESTION_USERS_SUCCESS = Symbol('FETCHED_SUGGESTION_USERS_SUCCESS');
export const FETCHED_USER_DETAILS_SUCCESS = Symbol('FETCHED_USER_DETAILS_SUCCESS');
export const CHANGE_MODE = Symbol('CHANGE_MODE');
export const UPDATE_SELECTED_USER = Symbol('UPDATE_SELECTED_USER');
export const CLEAR_SELECTED_USER = Symbol('CLEAR_SELECTED_USER');
export const FETCHED_ROLES_SUCCESS = Symbol('FETCHED_ROLES_SUCCESS');
export const FETCHED_DASHBOARD_USER_TITLES_SUCCESS = Symbol('FETCHED_DASHBOARD_USER_TITLES_SUCCESS');
export const SAVING_USER = Symbol('SAVING_USER');
export const SAVED_USER_FAILURE = Symbol('SAVED_USER_FAILURE');
export const SAVED_USER_SUCCESS = Symbol('SAVED_USER_SUCCESS');
export const UPDATE_MODULE_FIELD = Symbol('UPDATE_MODULE_FIELD');
export const UPDATE_TITLE_FIELD = Symbol('UPDATE_TITLE_FIELD');
export const UPDATE_SUPERVISOR_FIELD = Symbol('UPDATE_SUPERVISOR_FIELD');
export const UPDATE_LOGIN_ATTEMPTS = Symbol('UPDATE_LOGIN_ATTEMPTS');
export const CLOSE_SUGGESTION = Symbol('CLOSE_SUGGESTION');
export const CLOSE_TITLE_SUGGESTION = Symbol('CLOSE_TITLE_SUGGESTION');
export const FETCHED_REPORT_SUCCESS = Symbol('FETCHED_REPORT_SUCCESS');
export const LOADED_TITLES = Symbol('LOADED_TITLES');
export const LOADING_TITLES = Symbol('LOADING_TITLES');
export const LOADED_TITLES_FAIL = Symbol('LOADED_TITLES_FAIL');
export const OPEN_DROPDOWN_MENU_TITLES = Symbol('OPEN_DROPDOWN_MENU_TITLES');

/**
 * Request user list from server.
 *
 * @returns Action.
 */
export function loadReports() {
  return {
    [CALL_API]: {
      method: 'get',
      path: '/api/v1/users/reports',
      successType: FETCHED_REPORT_SUCCESS,
    },
  };
}

const USER_LIST_PATH = '/api/v1/users/list';

/**
 * Build query params of the user list request from the filters.
 *
 * @param {Immutable.Map} filters User list filters.
 * @returns {object} Query params.
 */
function getUserListQuery(filters) {
  const query = {
    search: filters.get('search'),
    category_title: filters.get('categoryTitle'),
    role_id: filters.get('roleId'),
    module_id: filters.get('moduleId'),
    supervisor_id: filters.get('supervisorId'),
    sort: filters.get('sort'),
    sort_order: filters.get('sortOrder'),
  };

  if (filters.get('includeInactive')) {
    query.include_inactive = 1;
  }

  return Object.fromEntries(Object.entries(query).filter(([, value]) => value));
}

/**
 * Request filtered user list from server.
 *
 * @param {Immutable.Map} filters User list filters.
 * @returns {object} Action.
 */
export function loadUsers(filters) {
  return {
    [CALL_API]: {
      method: 'get',
      path: USER_LIST_PATH,
      query: getUserListQuery(filters),
      unifier: 'get user list',
      dislodging: true,
      startType: FETCHING_USERS,
      errorType: FETCHED_USERS_FAILURE,
      successType: FETCHED_USERS_SUCCESS,
    },
  };
}

/**
 * Request all users (including inactive) for module/supervisor suggestions.
 *
 * @returns {object} Action.
 */
export function loadSuggestionUsers() {
  return {
    [CALL_API]: {
      method: 'get',
      path: USER_LIST_PATH,
      query: { include_inactive: 1 },
      unifier: 'get suggestion users',
      dislodging: true,
      successType: FETCHED_SUGGESTION_USERS_SUCCESS,
    },
  };
}

/**
 * Request full user data and select the user.
 *
 * @param {number} id User's id.
 * @returns {object} Action.
 */
export function loadUserDetails(id) {
  return {
    [CALL_API]: {
      method: 'get',
      path: `/api/v1/users/${id}`,
      unifier: 'get user details',
      dislodging: true,
      successType: FETCHED_USER_DETAILS_SUCCESS,
    },
  };
}

/**
 * Create an action for updating user list filters.
 *
 * @param {Immutable.Map} filters User list filters.
 * @returns {object} Action.
 */
export function setUserListFilters(filters) {
  return {
    type: SET_USER_LIST_FILTERS,
    filters,
  };
}

/**
 * Request the user titles list from the server.
 */
export function loadTitles() {
  return {
    [CALL_API]: {
      method: 'get',
      path: '/api/v1/users/titles',
      unifier: params => `${params.path}`,
      maxCount: 2,
      startType: LOADING_TITLES,
      successType: LOADED_TITLES,
      errorType: LOADED_TITLES_FAIL,
    },
  };
}

/**
 * Create an action for updating the supervisor field of a selected user.
 *
 * @param moduleId {number} Module's id.
 * @param supervisor {string} Module's name.
 * @returns Action.
 */
export function updateSupervisor(supervisorId, supervisor) {
  return {
    type: UPDATE_SUPERVISOR_FIELD,
    supervisorId,
    supervisor,
  };
}

/**
 * Create an action for updating title field.
 *
 * @param titleId {number} Title's id.
 * @param title {string} Title's name.
 * @returns Action.
 */
export function updateTitle(titleId, title) {
  return {
    type: UPDATE_TITLE_FIELD,
    titleId,
    title,
  };
}

/**
 * Open the dropdown menu title's list.
 *
 * @returns Action.
 */
export function openDropdownMenuTitles() {
  return {
    type: OPEN_DROPDOWN_MENU_TITLES,
  };
}

/**
 * Create an action for updating the module field of the selected user.
 *
 * @param moduleId {number} Module's id.
 * @param module {string} Module's name.
 * @returns Action.
 */
export function updateModule(moduleId, module) {
  return {
    type: UPDATE_MODULE_FIELD,
    moduleId,
    module,
  };
}

/**
 * Create an action for updating login attempts.
 *
 * @param checked {Boolean} Set to true will reset login attempts.
 * @returns Action.
 */
export function updateAttempts(checked) {
  return {
    type: UPDATE_LOGIN_ATTEMPTS,
    checked,
  };
}

/**
 * Create an action for calling api to update or insert selected user to database.
 *
 * @param {Immutable.Map} userData User data.
 * @param afterSuccess {Function}.
 * @param afterError {Function}.
 */
export function updateOrInsertUser(userData, { afterSuccess, afterError }) {
  const { id } = userData;
  const isUpdateRequest = id > 0;

  return {
    [CALL_API]: {
      method: isUpdateRequest ? 'put' : 'post',
      path: isUpdateRequest ? `/api/v1/users/${id}` : '/api/v1/users',
      body: userData,
      startType: SAVING_USER,
      errorType: SAVED_USER_FAILURE,
      successType: SAVED_USER_SUCCESS,
      skipGlobalErrorHandler: true,
      afterSuccess,
      afterError,
    },
  };
}

/**
 * Create an action for updating selected user in redux state.
 *
 * @param name {string} Name of selectedUser field.
 * @param value {any} Value of selectedUser field.
 * @returns Action.
 */
export function updateSelectedUser(name, value, checked) {
  return {
    type: UPDATE_SELECTED_USER,
    name,
    value,
    checked,
  };
}

/**
 * Create an action for deleting selected user in redux state.
 *
 * @param selectedUser {Immutable.Map} User data.
 * @returns Action.
 */
export function clearSelectedUser() {
  return {
    type: CLEAR_SELECTED_USER,
  };
}

/**
 * Remove Google 2FA.
 *
 * @param {number} userId Id of user.
 * @param afterSuccess {Function}.
 */
export function deleteGoogle2FAAuth({ userId, afterSuccess }) {
  return {
    [CALL_API]: {
      method: 'delete',
      path: `/api/v1/users/${userId}/google2fa`,
      afterSuccess,
    },
  };
}

/**
 * Create an action for changing form mode on the page (view|edit).
 *
 * @returns Action.
 */
export function changeMode(mode) {
  return {
    type: CHANGE_MODE,
    mode,
  };
}

/**
 * Create an action for calling api to fetch system roles.
 *
 * @returns Action.
 */
export function loadRoles() {
  return {
    [CALL_API]: {
      method: 'get',
      path: '/api/v1/users/roles',
      successType: FETCHED_ROLES_SUCCESS,
    },
  };
}

/**
 * Create an action for calling api to fetch dashboard user titles (title categories).
 *
 * @returns Action.
 */
export function loadDashboardUserTitles() {
  return {
    [CALL_API]: {
      method: 'get',
      path: '/api/v1/director_dashboard/user_titles',
      successType: FETCHED_DASHBOARD_USER_TITLES_SUCCESS,
    },
  };
}

/**
 * Close suggestion.
 *
 * @returns Action.
 */
export function closeSuggestion() {
  return {
    type: CLOSE_SUGGESTION,
  };
}

/**
 * Close the title suggestion, keeping module and supervisor as they are.
 *
 * @returns Action.
 */
export function closeTitleSuggestion() {
  return {
    type: CLOSE_TITLE_SUGGESTION,
  };
}
